import type { EcommerceCompany } from '@/api/ecommerce';

import type { EcommerceFormValues } from '../schema';
import type { UseFormReturn } from 'react-hook-form';

export type CompanyInvoiceValues = Pick<
  EcommerceFormValues,
  | 'tax_id'
  | 'company_name'
  | 'invoice_street'
  | 'invoice_building_number'
  | 'invoice_apartment_number'
  | 'invoice_postal_code'
  | 'invoice_city'
>;

type CompanyDataField = Exclude<keyof CompanyInvoiceValues, 'tax_id'>;

const COMPANY_DATA_FIELDS: CompanyDataField[] = [
  'company_name',
  'invoice_street',
  'invoice_building_number',
  'invoice_apartment_number',
  'invoice_postal_code',
  'invoice_city',
];

const REQUIRED_COMPANY_DATA_FIELDS: CompanyDataField[] = [
  'company_name',
  'invoice_street',
  'invoice_building_number',
  'invoice_postal_code',
  'invoice_city',
];

type InvoiceForm = Pick<UseFormReturn<EcommerceFormValues>, 'setValue'>;

type InvoiceResetForm = Pick<
  UseFormReturn<EcommerceFormValues>,
  'setValue' | 'clearErrors'
>;

export const normalizeTaxId = (taxId: string) => taxId.replace(/[\s-]/g, '');

export const emptyCompanyInvoice = (taxId: string): CompanyInvoiceValues => ({
  tax_id: taxId,
  company_name: '',
  invoice_street: '',
  invoice_building_number: '',
  invoice_apartment_number: '',
  invoice_postal_code: '',
  invoice_city: '',
});

export const companyToInvoice = (
  company: EcommerceCompany
): CompanyInvoiceValues => ({
  tax_id: company.nip,
  company_name: company.company_name,
  invoice_street: company.street ?? '',
  invoice_building_number: company.building_number ?? '',
  invoice_apartment_number: company.apartment_number ?? '',
  invoice_postal_code: company.postal_code ?? '',
  invoice_city: company.city ?? '',
});

export const pickCompanyInvoice = (
  values: CompanyInvoiceValues
): CompanyInvoiceValues => ({
  tax_id: values.tax_id ?? '',
  company_name: values.company_name ?? '',
  invoice_street: values.invoice_street ?? '',
  invoice_building_number: values.invoice_building_number ?? '',
  invoice_apartment_number: values.invoice_apartment_number ?? '',
  invoice_postal_code: values.invoice_postal_code ?? '',
  invoice_city: values.invoice_city ?? '',
});

export const missingCompanyDataFields = (
  values: Partial<CompanyInvoiceValues>
) => REQUIRED_COMPANY_DATA_FIELDS.filter((field) => !values[field]?.trim());

export const isCompanyInvoiceComplete = (
  values: Partial<CompanyInvoiceValues>
) => missingCompanyDataFields(values).length === 0;

export const formatCompanyAddress = (values: CompanyInvoiceValues) => {
  const streetLine = [
    values.invoice_street,
    [values.invoice_building_number, values.invoice_apartment_number]
      .filter(Boolean)
      .join('/'),
  ]
    .filter(Boolean)
    .join(' ');
  const cityLine = [values.invoice_postal_code, values.invoice_city]
    .filter(Boolean)
    .join(' ');

  return [streetLine, cityLine].filter(Boolean).join(', ');
};

// company_name idzie po adresie: jego walidacja sprawdza komplet danych firmy.
const APPLY_ORDER: (keyof CompanyInvoiceValues)[] = [
  'invoice_street',
  'invoice_building_number',
  'invoice_apartment_number',
  'invoice_postal_code',
  'invoice_city',
  'company_name',
  'tax_id',
];

export const applyCompanyInvoice = (
  { setValue }: InvoiceForm,
  values: CompanyInvoiceValues
) => {
  APPLY_ORDER.forEach((field) => {
    setValue(field, values[field], { shouldDirty: true, shouldValidate: true });
  });
};

export const clearCompanyData = ({ setValue }: InvoiceForm) => {
  COMPANY_DATA_FIELDS.forEach((field) => {
    setValue(field, '', { shouldDirty: true });
  });
};

// Zakup dla osoby prywatnej: dane firmy (z NIP-em) nie mogą zostać w formularzu
// ani wrócić po ponownym wyborze „Dla firmy”, a ich błędy — wisieć ukryte.
export const clearCompanyInvoice = ({
  setValue,
  clearErrors,
}: InvoiceResetForm) => {
  const fields: (keyof CompanyInvoiceValues)[] = [
    'tax_id',
    ...COMPANY_DATA_FIELDS,
  ];
  fields.forEach((field) => {
    setValue(field, '', { shouldDirty: true });
  });
  clearErrors(fields);
};
